import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, Phone, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsOfService = () => {
  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '200px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.05) 0%, rgba(0, 0, 0, 0) 100%)',
          padding: '60px 7.6923%',
          borderBottom: '1px solid var(--border-subtle)'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <Link to="/" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              marginBottom: '30px',
              fontSize: '14px'
            }}>
              <ArrowLeft size={16} />
              Back to Home
            </Link>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <FileText size={40} color="var(--brand-primary)" />
              <h1 className="heading-1" style={{ margin: 0 }}>Terms of Service</h1>
            </div>
            
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              VDM Business Innovations
            </p>
            <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
              <strong>Effective Date:</strong> February 8, 2026 | <strong>Last Updated:</strong> February 8, 2026
            </p>
          </div>
        </section>

        {/* Content */}
        <section style={{ padding: '60px 7.6923%' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            
            {/* Section 1 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>1. Introduction and Acceptance of Terms</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                Welcome to VDM Business Innovations ("Company," "we," "us," or "our"). These Terms of Service constitute a legally binding agreement between you ("User," "you," or "your") and VDM Business Innovations governing your access to and use of our website (https://vdmbi.org), services, and business operations.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                By accessing our website, using our services, or engaging with VDM Business Innovations in any capacity, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service, our Privacy Policy, and all applicable laws and regulations of India.
              </p>
              <div style={{ 
                background: 'rgba(255, 107, 107, 0.1)', 
                border: '1px solid rgba(255, 107, 107, 0.3)', 
                padding: '16px', 
                marginBottom: '16px' 
              }}>
                <p className="body-medium" style={{ color: '#FF6B6B', fontWeight: '600' }}>
                  IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT ACCESS OR USE OUR WEBSITE OR SERVICES.
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>2. About VDM Business Innovations</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                VDM Business Innovations is a business development and consulting organization registered in India, with headquarters in Bengaluru, Karnataka. We provide specialized services through multiple business divisions:
              </p>
              <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '24px' }}>
                <li><strong>Zenthara:</strong> Commercial real estate leasing, retail space consulting, mall management partnerships</li>
                <li><strong>Nuvora:</strong> Environmental solutions including water treatment systems, waste management, and sustainability consulting</li>
                <li><strong>Maitreya:</strong> Conversational AI application development and deployment</li>
                <li><strong>PivotMint:</strong> Career assessment and professional development applications</li>
                <li><strong>AnahatCare:</strong> Corporate Social Responsibility initiatives focusing on medical treatment accessibility</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>3. Definitions</h2>
              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ borderLeft: '3px solid var(--brand-primary)', paddingLeft: '16px' }}>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}><strong>"Services"</strong> means all business consulting, leasing, environmental solutions, AI applications, and related services provided by VDM Business Innovations</p>
                </div>
                <div style={{ borderLeft: '3px solid var(--brand-primary)', paddingLeft: '16px' }}>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}><strong>"Website"</strong> refers to https://vdmbi.org and all associated subdomains and pages</p>
                </div>
                <div style={{ borderLeft: '3px solid var(--brand-primary)', paddingLeft: '16px' }}>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}><strong>"Content"</strong> includes all text, graphics, images, logos, data, software, and materials available through our Website or Services</p>
                </div>
                <div style={{ borderLeft: '3px solid var(--brand-primary)', paddingLeft: '16px' }}>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}><strong>"Client"</strong> refers to any individual or entity that engages VDM Business Innovations for paid services</p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>4. User Eligibility and Account Registration</h2>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>4.1 Age and Legal Capacity</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                You must be at least 18 years of age and legally capable of entering into binding contracts under Indian law to use our Services.
              </p>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>4.2 Account Security</h3>
              <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '24px' }}>
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security and confidentiality of your login credentials</li>
                <li>Notify us immediately of any unauthorized access or security breach</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
            </div>

            {/* Section 5 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>5. Scope of Services</h2>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Consulting and Advisory Services</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Market analysis, feasibility studies, property evaluation, facility management consulting, strategic business development</p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Commercial Real Estate (Zenthara)</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Retail space leasing, mall management partnerships, lease negotiation, market intelligence, brand positioning</p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Environmental Solutions (Nuvora)</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>STP design and implementation, water treatment, waste management, ESG compliance consulting</p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Technology Applications</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Conversational AI (Maitreya), career assessment tools (PivotMint), custom business applications</p>
                </div>
              </div>
            </div>

            {/* Section 6 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>6. Acceptable Use Policy</h2>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>Prohibited Activities</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                You agree NOT to engage in any of the following:
              </p>
              <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '24px' }}>
                <li>Violating any applicable laws or regulations</li>
                <li>Infringing upon intellectual property rights</li>
                <li>Transmitting viruses, malware, or malicious code</li>
                <li>Attempting to gain unauthorized access to our systems</li>
                <li>Interfering with or disrupting the Website or Services</li>
                <li>Harassing, threatening, or defaming any party</li>
                <li>Using Services for illegal activities or fraud</li>
              </ul>
            </div>

            {/* Section 7 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>7. Intellectual Property Rights</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                All Content, materials, software, applications, designs, logos, trademarks, and intellectual property available through our Website and Services are owned by or licensed to VDM Business Innovations and are protected by Indian and international intellectual property laws.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Our intellectual property includes: VDM Business Innovations name, logo, and branding; Division names (Zenthara, Nuvora, Maitreya, PivotMint, AnahatCare); Website design and user interface; Proprietary methodologies and business processes.
              </p>
            </div>

            {/* Section 8 - Payment */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>8. Payment Terms and Financial Arrangements</h2>
              <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(0, 255, 209, 0.1)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Service Type</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Compensation Model</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Typical Terms</th>
                    </tr>
                  </thead>
                  <tbody style={{ color: 'var(--text-secondary)' }}>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Retail Space Leasing</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Commission-based</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>1 month's rent upon lease execution</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Consulting Services</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Fixed fee or retainer</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Monthly/quarterly invoicing</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Environmental Solutions</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Project-based pricing</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Milestone-based payments</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Technology Applications</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Development + licensing</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Upfront + recurring fees</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Payment due within 15 days of invoice date. Late payments subject to interest at 1.5% per month. All payments subject to GST and applicable taxes as per Indian law.
              </p>
            </div>

            {/* Section 11 - Confidentiality */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>11. Confidentiality and Non-Disclosure</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                Both parties acknowledge that during the course of business, they may have access to confidential and proprietary information including business strategies, client lists, proprietary methodologies, technical data, and pricing models.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Confidentiality obligations survive termination of services and continue for a period of 5 years unless the information constitutes a trade secret.
              </p>
            </div>

            {/* Section 12 - Disclaimers */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>12. Disclaimers and Limitations of Liability</h2>
              <div style={{ 
                background: 'rgba(255, 107, 107, 0.05)', 
                border: '1px solid rgba(255, 107, 107, 0.3)', 
                padding: '20px', 
                marginBottom: '16px' 
              }}>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  OUR WEBSITE AND SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. We do not warrant or guarantee specific business outcomes, successful lease execution, or achievement of environmental performance targets.
                </p>
              </div>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                <strong>Total Liability Cap:</strong> Our total aggregate liability shall not exceed the total fees paid by you in the 12 months immediately preceding the event giving rise to liability.
              </p>
            </div>

            {/* Section 14 - Dispute Resolution */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>14. Dispute Resolution and Governing Law</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                These Terms shall be governed by and construed in accordance with the laws of India. The courts of Bengaluru, Karnataka, India shall have exclusive jurisdiction over any disputes.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                <strong>Arbitration:</strong> If informal resolution fails, disputes shall be resolved through arbitration under the Arbitration and Conciliation Act, 1996. Seat of arbitration: Bengaluru, Karnataka, India. Language: English.
              </p>
            </div>

            {/* Section 18 - Grievance */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>18. Grievance Redressal</h2>
              <div style={{ 
                background: 'rgba(0, 255, 209, 0.05)', 
                border: '1px solid var(--brand-primary)', 
                padding: '24px' 
              }}>
                <h4 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>Grievance Officer</h4>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  <Mail size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Email: sandeep@vdmbi.org
                </p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  <Phone size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Phone: +91 8050518788
                </p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Address: VDM Business Innovations, Bengaluru, Karnataka, India
                </p>
                <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                  Response Time: Acknowledgment within 72 hours; resolution within 30 days
                </p>
              </div>
            </div>

            {/* Contact Section */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>Contact Us</h2>
              <div style={{ 
                background: 'rgba(255,255,255,0.03)', 
                border: '1px solid var(--border-subtle)', 
                padding: '24px' 
              }}>
                <p className="body-medium" style={{ color: 'var(--text-primary)', marginBottom: '16px' }}><strong>VDM Business Innovations</strong></p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Registered Address: Bengaluru, Karnataka, India</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Email: sandeep@vdmbi.org</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Phone: +91 8050518788</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>Business Hours: Monday to Saturday, 9:00 AM to 6:00 PM IST</p>
              </div>
            </div>

            {/* Document Info */}
            <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid var(--border-subtle)' }}>
              <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                Document Version: 1.0 | Policy Owner: VDM Business Innovations | Review Cycle: Annual | Next Review Date: February 2027
              </p>
              <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '8px' }}>
                Legal Compliance: Information Technology Act 2000, DPDP Act 2023, Consumer Protection Act 2019, Indian Contract Act 1872
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfService;
